import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { OptimizedImage } from "@/components/ui/optimized-image";
import {
  Check,
  Calendar,
  Clock,
  MapPin,
  Users,
  Mail,
  ArrowRight,
  Leaf,
  PartyPopper,
} from "lucide-react";

// Note: hike_experience.png doesn't exist, using crafting process image as fallback
import workshopImage from "@assets/generated_images/terrarium_crafting_process.png";
const hikeImage = workshopImage;

interface BookingDetails {
  id: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  workshopDate: string;
  packageType: string;
  price: number;
  status: string;
  specialRequests?: string;
}

export default function WorkshopSuccess() {
  const [, setLocation] = useLocation();
  const [confirmed, setConfirmed] = useState(false);

  // Get query params
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking_id");

  // Fetch booking details
  const { data: booking, isLoading } = useQuery<BookingDetails>({
    queryKey: ["/api/workshop/booking", bookingId],
    queryFn: async () => {
      if (!bookingId) throw new Error("No booking ID");
      const response = await fetch(`/api/workshop/booking/${bookingId}`);
      if (!response.ok) throw new Error("Failed to fetch booking");
      return response.json();
    },
    enabled: !!bookingId,
  });

  // Confirm booking mutation
  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!bookingId) throw new Error("No booking ID");
      return apiRequest("POST", `/api/workshop/confirm/${bookingId}`, { sessionId });
    },
    onSuccess: () => {
      setConfirmed(true);
    },
  });

  // Auto-confirm on load
  useEffect(() => {
    if (bookingId && sessionId && !confirmed) {
      confirmMutation.mutate();
    }
  }, [bookingId, sessionId]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const getPackageName = (type: string) => {
    return type === "2-person"
      ? "Duo Workshop Experience (2 people)"
      : "Group Workshop Experience (4 people)";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your booking...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30">
        <Card className="p-8 max-w-md text-center">
          <h2 className="text-xl font-semibold mb-2">Booking Not Found</h2>
          <p className="text-muted-foreground mb-4">
            We couldn't find your booking. Please contact us if you need assistance.
          </p>
          <Button onClick={() => setLocation("/workshop")}>
            Book a Workshop
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Hero Section */}
      <div className="relative h-48 md:h-64 overflow-hidden">
        <OptimizedImage
          src={hikeImage}
          alt="Workshop Experience"
          fill
          objectFit="cover"
          priority
          className="absolute inset-0"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background z-10" />
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="text-center text-white">
            <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl font-bold">
              Booking Confirmed!
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Success Message */}
        <Card className="p-6 mb-8 bg-green-50 border-green-200">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
              <PartyPopper className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-lg text-green-800 mb-1">
                You're all set, {booking.customerName.split(" ")[0]}!
              </h2>
              <p className="text-green-700">
                Your terrarium workshop experience has been booked. We've sent a
                confirmation email to{" "}
                <span className="font-medium">{booking.customerEmail}</span>
              </p>
            </div>
          </div>
        </Card>

        {/* Booking Details */}
        <Card className="p-6 mb-8">
          <h3 className="font-serif text-xl font-semibold mb-6">
            Booking Details
          </h3>

          <div className="space-y-6">
            {/* Package */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Package</p>
                <p className="font-medium">{getPackageName(booking.packageType)}</p>
              </div>
            </div>

            <Separator />

            {/* Date */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Date</p>
                <p className="font-medium">{formatDate(booking.workshopDate)}</p>
              </div>
            </div>

            <Separator />

            {/* Time */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Time</p>
                <p className="font-medium">9:00 AM - 5:00 PM (approximately)</p>
              </div>
            </div>

            <Separator />

            {/* Location */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Location</p>
                <p className="font-medium">Denver, Colorado area</p>
                <p className="text-sm text-muted-foreground">
                  Exact meeting location will be sent via email
                </p>
              </div>
            </div>

            <Separator />

            {/* Confirmation */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Confirmation sent to</p>
                <p className="font-medium">{booking.customerEmail}</p>
              </div>
            </div>

            <Separator />

            {/* Total */}
            <div className="flex items-center justify-between pt-2">
              <span className="text-lg font-medium">Total Paid</span>
              <span className="font-serif text-2xl font-bold text-primary">
                ${booking.price.toLocaleString()}
              </span>
            </div>
          </div>
        </Card>

        {/* What to Bring */}
        <Card className="p-6 mb-8 bg-muted/50">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Leaf className="w-5 h-5 text-primary" />
            What to Bring
          </h3>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              Comfortable hiking shoes
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              Weather-appropriate clothing
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              Water bottle
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              Excitement and creativity!
            </li>
          </ul>
          <p className="text-sm text-muted-foreground mt-4">
            Lunch and all terrarium materials are provided.
          </p>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button variant="outline" onClick={() => setLocation("/")}>
            Return Home
          </Button>
          <Button onClick={() => setLocation("/checkout")} className="gap-2">
            Order a Terrarium
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
